import { useState, useCallback } from "react";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { MESSAGES } from "../messages";
import {
  EngagementAssessmentV2Run,
  GetEngagementAssessmentV2Request,
} from "../types/engagementAssessmentV2Types";

interface UseGetEngagementAssessmentV2Return {
  runs: EngagementAssessmentV2Run[];
  isLoading: boolean;
  error: string | null;
  fetchHistory: (engagementRecordId: string) => Promise<void>;
}

const sortRunsDesc = (
  runs: unknown,
): EngagementAssessmentV2Run[] => {
  if (!Array.isArray(runs)) return [];
  return [...(runs as EngagementAssessmentV2Run[])].sort(
    (a, b) =>
      new Date(b.assessed_at).getTime() - new Date(a.assessed_at).getTime(),
  );
};

export function useGetEngagementAssessmentV2(): UseGetEngagementAssessmentV2Return {
  const { addToast } = useToast();
  const [runs, setRuns] = useState<EngagementAssessmentV2Run[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { sendData, isLoading } = usePost<
    EngagementAssessmentV2Run[],
    GetEngagementAssessmentV2Request
  >();

  const fetchHistory = useCallback(
    async (engagementRecordId: string): Promise<void> => {
      setError(null);
      const result = await sendData("AI_GET_ENGAGEMENT_ASSESSMENT_V2", {
        engagement_record_id: engagementRecordId,
      });
      if (result === null) {
        const msg =
          MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.HISTORY_FETCH_FAILED;
        setError(msg);
        addToast(msg, "error");
        return;
      }
      setRuns(sortRunsDesc(result));
    },
    [sendData, addToast],
  );

  return { runs, isLoading, error, fetchHistory };
}
